import { z } from 'zod';
import { Classification } from './artifact.ts';
import { IsoDateTime, TenantId } from './common.ts';

/**
 * Added by 21 step 0 (brief 21 §1, §1b.7; ADR-021): what kind of authority a knowledge source carries.
 */
export const KnowledgeAuthority = z
  .enum(['law', 'regulation', 'policy', 'guidance'])
  .meta({ id: 'KnowledgeAuthority' });
export type KnowledgeAuthority = z.infer<typeof KnowledgeAuthority>;

/**
 * Added by 21 step 0 (§1b.7): provenance metadata of a knowledge source, from seed front matter. The platform
 * resolves "which version applied at time T" from `effectiveFrom`/`effectiveTo`; packs own the values (core holds no
 * jurisdiction value). The freshness sweep treats a passed `effectiveTo` as a stale trigger (21c).
 */
export const KnowledgeSourceMetadata = z
  .object({
    /** Pack-defined jurisdiction code (e.g. a country or emirate code); never interpreted by core. */
    jurisdiction: z.string().min(1).max(100).optional(),
    effectiveFrom: IsoDateTime.optional(),
    effectiveTo: IsoDateTime.optional(),
    authority: KnowledgeAuthority.optional(),
    /** True when the content must be validated against a local (jurisdiction) source before use. */
    validateLocally: z.boolean().optional(),
    /** References to the legal or policy basis (article ids, URLs). */
    basisRefs: z.array(z.string().min(1).max(500)).optional(),
  })
  .meta({ id: 'KnowledgeSourceMetadata' });
export type KnowledgeSourceMetadata = z.infer<typeof KnowledgeSourceMetadata>;

/** A knowledge source (document) as listed by `GET /knowledge/sources`. */
export const KnowledgeSource = z
  .object({
    id: z.string().min(1),
    tenantId: TenantId,
    scope: z.string().min(1),
    name: z.string().min(1),
    kind: z.enum(['file', 'text', 'url']),
    ownerSubject: z.string().min(1),
    aclSubjects: z.array(z.string()),
    classification: Classification,
    version: z.number().int().positive(),
    status: z.enum(['ingesting', 'ready', 'failed', 'deleted']),
    artifactKey: z.string().optional(),
    hash: z.string().optional(),
    chunkCount: z.number().int().nonnegative(),
    lastVerifiedAt: IsoDateTime.optional(),
    /** When the freshness sweep found the source unverified for longer than the window; absent while fresh. */
    staleAt: IsoDateTime.optional(),
    /** Added by 21 step 0 (§1b.7): provenance metadata (all optional; absent for sources seeded before 21). */
    jurisdiction: KnowledgeSourceMetadata.shape.jurisdiction,
    effectiveFrom: KnowledgeSourceMetadata.shape.effectiveFrom,
    effectiveTo: KnowledgeSourceMetadata.shape.effectiveTo,
    authority: KnowledgeSourceMetadata.shape.authority,
    validateLocally: KnowledgeSourceMetadata.shape.validateLocally,
    basisRefs: KnowledgeSourceMetadata.shape.basisRefs,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'KnowledgeSource' });
export type KnowledgeSource = z.infer<typeof KnowledgeSource>;
